import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Phone, MessageCircle, Building2, Star, X, ArrowLeft, Copy, Contact as ContactIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { customersApi, ContactSearchResult } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/** Digits that identify the subscriber — the last 10 for an Indian mobile. */
function digitsOf(raw: string | null | undefined): string {
  const d = String(raw || '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
}

function initials(name: string | null, company: string) {
  const source = (name || company || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_TONES = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-rose-100 text-rose-700',
];

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const run = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    try {
      const res = await customersApi.searchContacts(term.trim());
      setResults(res.data || []);
      setSearched(true);
    } catch (e: any) {
      const offline = e instanceof TypeError || /failed to fetch/i.test(e?.message || '');
      showError(
        offline ? 'No connection' : 'Search failed',
        offline ? 'Could not reach the server.' : e?.message || 'Something went wrong'
      );
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Debounced so typing a company name does not fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => run(query), 300);
    return () => clearTimeout(t);
  }, [query, run]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copied', text);
    } catch {
      showError('Could not copy', 'Clipboard is blocked on this device.');
    }
  };

  // Group alphabetically by whatever the row is actually titled, so the list
  // reads like a phonebook rather than an unordered result dump.
  const grouped = useMemo(() => {
    const out: { letter: string; rows: ContactSearchResult[] }[] = [];
    const sorted = [...results].sort((a, b) => {
      const an = (a.contact_person || a.company || '').toLowerCase();
      const bn = (b.contact_person || b.company || '').toLowerCase();
      return an.localeCompare(bn);
    });
    for (const r of sorted) {
      const title = r.contact_person || r.company || '?';
      const letter = /[a-z]/i.test(title[0]) ? title[0].toUpperCase() : '#';
      const last = out[out.length - 1];
      if (last && last.letter === letter) last.rows.push(r);
      else out.push({ letter, rows: [r] });
    }
    return out;
  }, [results]);

  return (
    <div className="pb-20">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="flex items-center gap-2 px-3 py-2">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 text-gray-500 md:hidden" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Person, company or mobile number"
              className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults([]); setSearched(false); inputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400"
                aria-label="Clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {searched && !loading && (
          <div className="px-3 pb-2 text-xs text-gray-400">
            {results.length === 0
              ? 'No matches'
              : `${results.length} ${results.length === 1 ? 'contact' : 'contacts'}`}
          </div>
        )}
      </div>

      {loading && <div className="p-6 text-center text-sm text-gray-400">Searching...</div>}

      {!loading && !searched && (
        <div className="p-10 text-center">
          <ContactIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400">
            Search your customer contacts
          </p>
          <p className="text-xs text-gray-300 mt-1">
            Type a person's name, a company, or any part of a mobile number
          </p>
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <div className="p-10 text-center text-sm text-gray-400">
          Nothing found for &ldquo;{query}&rdquo;.
        </div>
      )}

      {grouped.map(group => (
        <div key={group.letter}>
          <div className="px-3 py-1 text-xs font-semibold text-gray-400 bg-gray-50">
            {group.letter}
          </div>
          {group.rows.map((r, i) => {
            const digits = digitsOf(r.mobile_no);
            const title = r.contact_person || r.company;
            const tone = AVATAR_TONES[(r.customer_id + i) % AVATAR_TONES.length];
            return (
              <div
                key={`${r.customer_id}-${r.mobile_no}-${i}`}
                className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-50"
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${tone}`}>
                  {initials(r.contact_person, r.company)}
                </div>

                <button
                  onClick={() => navigate('/search', { state: { customerId: r.customer_id } })}
                  className="flex-1 min-w-0 text-left"
                >
                  <span className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate">{title}</span>
                    {r.primary_contact && (
                      <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                    )}
                  </span>
                  {r.contact_person && (
                    <span className="flex items-center gap-1 text-xs text-blue-600">
                      <Building2 className="h-3 w-3 shrink-0" />
                      <span className="truncate">{r.company}</span>
                    </span>
                  )}
                  <span className="block text-xs text-gray-400">{r.mobile_no}</span>
                </button>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => copy(r.mobile_no)}
                    className="p-2 rounded-lg bg-gray-100 text-gray-500"
                    aria-label="Copy number"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <a
                    href={`https://wa.me/${digits.length === 10 ? '91' : ''}${digits}`}
                    target="_blank" rel="noreferrer"
                    className="p-2 rounded-lg bg-green-50 text-green-600"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </a>
                  <a
                    href={`tel:${r.mobile_no}`}
                    className="p-2 rounded-lg bg-emerald-50 text-emerald-600"
                    aria-label="Call"
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default Contacts;
